import { useState, useEffect } from 'react'
import { Plus, Pencil, Trash2 } from 'lucide-react'
import { AdminLayout, PageHeader } from '@/components/layout/AdminLayout'
import { Modal } from '@/components/ui/Modal'
import { Toggle } from '@/components/ui/Toggle'
import {
  fetchAdminConstraints, fetchAdminOptions, fetchAdminModels,
  createConstraint, updateConstraint, deleteConstraint,
} from '@/lib/api'
import type { OptionWithModels } from '@/lib/api'
import type { Constraint, Model } from '@/types'

const CONSTRAINT_TYPES = [
  { value: 'excludes',                 label: 'Excludes — selecting source deselects target' },
  { value: 'requires',                 label: 'Requires — selecting source requires target' },
  { value: 'incompatible_with_model',  label: 'Incompatible with Model — option unavailable on model' },
  { value: 'warning_only',            label: 'Warning Only — shows message, no enforcement' },
]

const EMPTY: Partial<Constraint> = {
  constraint_type: 'excludes',
  source_type: 'option', source_id: 0,
  target_type: 'option', target_id: 0,
  message: '', severity: 'error', is_active: 1,
}

function EntitySelect({
  type, value, options, models, onChange,
}: {
  type: string
  value: number
  options: OptionWithModels[]
  models: Model[]
  onChange: (v: number) => void
}) {
  const items = type === 'model' ? models : options
  return (
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      required
      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
    >
      <option value={0}>— select —</option>
      {items.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
    </select>
  )
}

function ConstraintForm({
  init, options, models, onSave, onClose,
}: {
  init: Partial<Constraint>
  options: OptionWithModels[]
  models: Model[]
  onSave: (c: Constraint) => void
  onClose: () => void
}) {
  const [form, setForm] = useState<Partial<Constraint>>({ ...EMPTY, ...init })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isNew = !form.id

  function set<K extends keyof Constraint>(key: K, val: Constraint[K]) {
    setForm((f) => ({ ...f, [key]: val }))
  }

  const isModelConstraint = form.constraint_type === 'incompatible_with_model'

  useEffect(() => {
    if (isModelConstraint) {
      setForm((f) => ({ ...f, target_type: 'model' }))
    } else {
      setForm((f) => ({ ...f, target_type: 'option' }))
    }
  }, [isModelConstraint])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      const saved = isNew
        ? await createConstraint(form)
        : await updateConstraint(form.id!, form)
      onSave(saved)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Constraint Type</label>
        <select
          value={form.constraint_type ?? 'excludes'}
          onChange={(e) => set('constraint_type', e.target.value)}
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
        >
          {CONSTRAINT_TYPES.map((ct) => (
            <option key={ct.value} value={ct.value}>{ct.label}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Source (Option)</label>
          <EntitySelect
            type="option"
            value={form.source_id ?? 0}
            options={options}
            models={models}
            onChange={(v) => set('source_id', v)}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">
            Target ({isModelConstraint ? 'Model' : 'Option'})
          </label>
          <EntitySelect
            type={isModelConstraint ? 'model' : 'option'}
            value={form.target_id ?? 0}
            options={options}
            models={models}
            onChange={(v) => set('target_id', v)}
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Message (shown to user)</label>
        <input
          type="text"
          value={form.message ?? ''}
          onChange={(e) => set('message', e.target.value)}
          required
          placeholder="e.g. Cedar siding is incompatible with the black roof option."
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Severity</label>
        <select
          value={form.severity ?? 'error'}
          onChange={(e) => set('severity', e.target.value)}
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
        >
          <option value="error">Error — hard block</option>
          <option value="warning">Warning — soft nudge</option>
        </select>
      </div>

      {!isNew && (
        <Toggle
          checked={!!form.is_active}
          onChange={(v) => set('is_active', v ? 1 : 0)}
          label="Active"
        />
      )}

      {error && <p className="text-red-600 text-sm">{error}</p>}

      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="px-5 py-2 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-600 disabled:opacity-50 transition-colors"
        >
          {saving ? 'Saving…' : isNew ? 'Create Constraint' : 'Save Changes'}
        </button>
      </div>
    </form>
  )
}

export default function AdminConstraints() {
  const [constraints, setConstraints] = useState<Constraint[]>([])
  const [options, setOptions] = useState<OptionWithModels[]>([])
  const [models, setModels] = useState<Model[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [modal, setModal] = useState<'add' | Constraint | null>(null)

  useEffect(() => {
    Promise.all([fetchAdminConstraints(), fetchAdminOptions(), fetchAdminModels()])
      .then(([cons, opts, mods]) => { setConstraints(cons); setOptions(opts); setModels(mods) })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false))
  }, [])

  const optionMap = Object.fromEntries(options.map((o) => [o.id, o.name]))
  const modelMap = Object.fromEntries(models.map((m) => [m.id, m.name]))

  function entityName(type: string, id: number) {
    return type === 'model' ? (modelMap[id] ?? `Model #${id}`) : (optionMap[id] ?? `Option #${id}`)
  }

  function handleSaved(c: Constraint) {
    setConstraints((prev) => {
      const idx = prev.findIndex((x) => x.id === c.id)
      return idx >= 0 ? prev.map((x) => x.id === c.id ? c : x) : [...prev, c]
    })
    setModal(null)
  }

  async function handleDelete(c: Constraint) {
    if (!confirm('Delete this constraint?')) return
    try {
      await deleteConstraint(c.id)
      setConstraints((prev) => prev.filter((x) => x.id !== c.id))
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Delete failed')
    }
  }

  return (
    <AdminLayout>
      <PageHeader
        title="Constraints"
        subtitle="Define incompatibilities, requirements, and warnings between options"
        action={
          <button
            onClick={() => setModal('add')}
            className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-600 transition-colors"
          >
            <Plus className="h-4 w-4" /> Add Constraint
          </button>
        }
      />

      {error && <div className="text-red-600 text-sm mb-4">{error}</div>}

      {loading ? (
        <div className="text-slate-400 text-sm">Loading…</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide">Type</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide hidden sm:table-cell">Source</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide hidden sm:table-cell">Target</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide hidden lg:table-cell">Message</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide">Status</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {constraints.map((con) => (
                <tr key={con.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50">
                  <td className="px-5 py-3 font-medium text-slate-800 capitalize whitespace-nowrap">
                    {con.constraint_type.replace(/_/g, ' ')}
                  </td>
                  <td className="px-5 py-3 text-slate-600 hidden sm:table-cell">{entityName(con.source_type, con.source_id)}</td>
                  <td className="px-5 py-3 text-slate-600 hidden sm:table-cell">{entityName(con.target_type, con.target_id)}</td>
                  <td className="px-5 py-3 text-slate-500 hidden lg:table-cell truncate max-w-xs">{con.message}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${con.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                      {con.is_active ? 'Active' : 'Off'}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => setModal(con)} className="text-slate-400 hover:text-brand-600 transition-colors">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => handleDelete(con)} className="text-slate-400 hover:text-red-600 transition-colors">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {constraints.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-8 text-center text-slate-400 text-sm">
                    No constraints defined yet. Add one to enforce option compatibility rules.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <Modal
          title={modal === 'add' ? 'Add Constraint' : 'Edit Constraint'}
          onClose={() => setModal(null)}
          size="lg"
        >
          <ConstraintForm
            init={modal === 'add' ? {} : (modal as Constraint)}
            options={options}
            models={models}
            onSave={handleSaved}
            onClose={() => setModal(null)}
          />
        </Modal>
      )}
    </AdminLayout>
  )
}
